import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BuyerContact,
  TextMessageOutcome,
  CollectionActivityReference,
  CollectionActivityWithUser,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  updateCollectionActivity,
  getBuyerCollectionActivities,
} from '../../../../../../redux/services/collectionActivityService';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import LexicalEditor from '../../../../../general/LexicalEditor/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';

interface EditTextMessageModalProps {
  open: boolean;
  activity: CollectionActivityWithUser | null;
  onClose: () => void;
  onSuccess?: () => void;
  contacts?: BuyerContact[];
}

const textMessageOutcomeOptions: { value: TextMessageOutcome; label: string }[] = [
  { value: 'DELIVERED', label: 'Delivered - Message was delivered' },
  { value: 'READ', label: 'Read - Message was read' },
  { value: 'NO_RESPONSE', label: 'No Response - No reply received' },
  { value: 'FAILED', label: 'Failed - Message could not be sent' },
];

function EditTextMessageModal({
  open,
  activity,
  onClose,
  onSuccess,
  contacts,
}: EditTextMessageModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [phone, setPhone] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [outcome, setOutcome] = useState<TextMessageOutcome>('DELIVERED');
  const [notes, setNotes] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [messageError, setMessageError] = useState('');

  const isLoading = loadingList.includes('updateCollectionActivity');

  // Derive initial notes from activity - memoized
  const initialNotes = useMemo(() => {
    if (!activity?.textMessageDetails?.notes) return undefined;
    const existingNotes = activity.textMessageDetails.notes;
    return typeof existingNotes === 'string' ? existingNotes : JSON.stringify(existingNotes);
  }, [activity?.textMessageDetails?.notes]);

  // Initialize form with existing text message details when activity changes
  useEffect(() => {
    if (activity?.textMessageDetails) {
      setPhone(activity.textMessageDetails.phoneNumber || '');
      setMessageContent(activity.textMessageDetails.messageContent || '');
      setOutcome(activity.textMessageDetails.outcome || 'DELIVERED');
    }
    setReferences(activity?.references || []);
  }, [activity]);

  const handleNotesChange = useCallback((content: string) => {
    setNotes(content);
  }, []);

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    if (!messageContent.trim()) {
      setMessageError('Please enter the message content');
      return;
    }

    if (!currentSupplier?.id || !activity) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build edit history entry for notes if they changed
      const existingNotes = activity.textMessageDetails?.notes;
      const notesContent = notes || initialNotes;
      const newNotes = notesContent ? JSON.parse(notesContent) : undefined;

      const notesChanged = JSON.stringify(existingNotes) !== JSON.stringify(newNotes);

      let editHistory = activity.textMessageDetails?.editHistory || [];

      if (notesChanged && existingNotes) {
        const editHistoryEntry = {
          previousContent: existingNotes,
          versionCreatedAt: activity.createdAt || new Date(),
          replacedAt: new Date(),
          replacedBy: user.id,
        };
        editHistory = [...editHistory, editHistoryEntry];
      }

      const result = await dispatch(
        updateCollectionActivity({
          supplierId: currentSupplier.id,
          activityId: activity.id,
          updateData: {
            textMessageDetails: {
              phoneNumber: phone,
              messageContent,
              outcome,
              notes: newNotes,
              editHistory,
            },
            references,
          },
        })
      );

      if (result.payload && (result.payload as any).success) {
        // Refresh the activities list
        dispatch(
          getBuyerCollectionActivities({
            supplierId: currentSupplier.id,
            buyerId: activity.buyerId,
          })
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update text message:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to update text message. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Edit Text Message"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Save Changes"
          />
        </>
      }>
      <AppInputField
        label="Phone Number"
        isRequired
        id="edit-text-phone"
        value={phone}
        onTextChange={(val) => {
          setPhone(val);
          if (phoneError) setPhoneError('');
        }}
        placeholder="+1 (555) 123-4567"
        isDisabled={isLoading}
        errorText={phoneError}
      />

      <AppDropDown
        label="Outcome"
        items={textMessageOutcomeOptions}
        value={outcome}
        onSelectChange={(selected) => setOutcome(selected?.value as TextMessageOutcome)}
        isDisabled={isLoading}
        placeholder="Select outcome"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Message Content <span className="text-red-500">*</span>
        </label>
        <textarea
          value={messageContent}
          onChange={(e) => {
            setMessageContent(e.target.value);
            if (messageError) setMessageError('');
          }}
          placeholder="Enter the text message content..."
          rows={4}
          disabled={isLoading}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all resize-y"
        />
        {messageError && <p className="mt-1 text-xs text-red-500">{messageError}</p>}
      </div>

      <LexicalEditor
        label="Notes (Optional)"
        initialContent={initialNotes}
        onChange={handleNotesChange}
        placeholder="Enter additional notes..."
        minHeight="100px"
        editable={!isLoading}
      />

      {activity?.textMessageDetails?.editHistory &&
        activity.textMessageDetails.editHistory.length > 0 && (
          <div className="text-xs text-gray-500">
            Notes have been edited {activity.textMessageDetails.editHistory.length} time(s)
          </div>
        )}

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={activity?.buyerId || ''}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={contacts}
      />
    </DillModal>
  );
}

export default EditTextMessageModal;
